import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import { MktCard, MktButton, Eyebrow } from './marketplaceUi';
import { BOOK_A_CALL_URL } from '../../config/constants';

/**
 * Help & FAQ. The copy is the same set of answers the marketing site publishes, so a
 * user is never told two different things about what BluePill is.
 *
 * Grouped into the four sections the marketing FAQ uses — the questions people ask
 * are about four distinct things (the panel, the Twins, the trial, the commercials),
 * and one flat list buries that.
 */
type FaqSection = {
  eyebrow: string;
  items: { q: string; a: string }[];
};

const FAQ_SECTIONS: FaqSection[] = [
  {
    eyebrow: 'About the study',
    items: [
      {
        q: 'Are these real people or fully synthetic?',
        a: 'Every Twin is built from a real human survey participant — not generated from statistical models. We conducted 60-minute in-depth surveys with 1,000 US breakfast consumers and built a Twin from each one.',
      },
      {
        q: 'How accurate are the AI Twins?',
        a: "In blind validation tests, Twin responses matched their source participant's real answers on more than 85% of repeat questions. They're built to reflect genuine preferences, not averaged personas.",
      },
      {
        q: 'How recent is the data?',
        a: 'Our current panel was surveyed within the last 90 days, and we refresh the underlying studies on a rolling quarterly basis so the Twins reflect up-to-date attitudes.',
      },
    ],
  },
  {
    eyebrow: 'About the twins',
    items: [
      {
        q: 'Can a Twin give a wrong answer?',
        a: "Yes — just like a real person, a Twin can be uncertain or inconsistent. That's intentional. We surface confidence signals so you can weigh responses rather than treating them as absolute fact.",
      },
      {
        q: "What's the difference between a Twin and a survey?",
        a: 'A survey gives you one fixed set of answers. A Twin lets you keep asking — follow-ups, new concepts, fresh stimuli — and get responses grounded in that same real participant, on demand.',
      },
    ],
  },
  {
    eyebrow: 'About the free trial',
    items: [
      {
        q: "What's included in the free study?",
        a: 'Your free study includes one full use case — packaging, concept, chat, or survey — run against the complete panel of 1,000 Twins, with shareable results.',
      },
      {
        q: 'Can I run the same use case twice?',
        a: 'Absolutely. You can re-run a use case as many times as you like to test variations, refine stimuli, or compare results side by side.',
      },
      {
        q: 'How long does it take to get results?',
        a: "Most studies return complete results in minutes, not weeks. Larger or multi-step studies may take a little longer, but you'll never wait days for fieldwork.",
      },
    ],
  },
  {
    eyebrow: 'About access and pricing',
    items: [
      {
        q: 'What happens after the free study?',
        a: "After your free study you can upgrade to a plan that fits your team's volume. There's no commitment — your free results stay accessible either way.",
      },
      {
        q: 'Is my data kept private?',
        a: 'Your stimuli, prompts, and results are private to your workspace and never used to train shared models. Participant identities remain fully anonymized.',
      },
    ],
  },
];

const FaqRow: React.FC<{ q: string; a: string; open: boolean; onToggle: () => void }> = ({
  q, a, open, onToggle,
}) => (
  <div className="border-b border-black/[0.08] last:border-b-0">
    <button
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[#f9fafb]"
    >
      <span className="text-[15px] font-medium text-[#111]">{q}</span>
      <ChevronDown
        className={`h-4 w-4 flex-shrink-0 text-[#9ca3af] transition-transform ${open ? 'rotate-180' : ''}`}
      />
    </button>
    {open && (
      <p className="px-5 pb-5 text-[14px] leading-relaxed text-[#6b7280]">{a}</p>
    )}
  </div>
);

const MarketplaceFaq: React.FC = () => {
  // Open the first question by default — an accordion where everything is shut reads
  // as an empty page. Keyed by "section:index" so one open row can't collide with a
  // row at the same index in another section.
  const [openKey, setOpenKey] = useState<string | null>('0:0');

  return (
    <MarketplaceShell maxWidth="max-w-3xl">
      <Eyebrow>Help &amp; FAQ</Eyebrow>
      <h1 className="mt-1 text-[28px] font-semibold text-[#111]">Questions, answered</h1>
      <p className="mt-1.5 text-[13px] text-[#6b7280]">
        Where the AI Consumer Twins come from, how far to trust them, and what your free study covers.
      </p>

      {FAQ_SECTIONS.map((section, s) => (
        <section key={section.eyebrow} className="mt-6">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6b7280]">
            {section.eyebrow}
          </div>
          <MktCard className="p-0">
            {section.items.map((item, i) => {
              const key = `${s}:${i}`;
              return (
                <FaqRow
                  key={item.q}
                  q={item.q}
                  a={item.a}
                  open={openKey === key}
                  onToggle={() => setOpenKey(openKey === key ? null : key)}
                />
              );
            })}
          </MktCard>
        </section>
      ))}

      <MktCard className="mt-5 flex flex-col gap-4 p-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div>
          <div className="text-[15px] font-medium text-[#111]">Still have a question?</div>
          <p className="mt-0.5 text-[13px] text-[#6b7280]">
            Talk to the team — we’ll walk you through it.
          </p>
        </div>
        <a href={BOOK_A_CALL_URL} target="_blank" rel="noopener noreferrer">
          <MktButton>Book a demo call</MktButton>
        </a>
      </MktCard>
    </MarketplaceShell>
  );
};

export default MarketplaceFaq;
